import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import { useState, useEffect, useRef } from 'react';
import { scroller } from 'react-scroll';
import { useDataProvider } from 'react-admin';
import DOMPurify from 'dompurify';
import {
  Card,
  CardContent,
  CardMedia,
  Typography,
  Box,
  Link,
  ButtonGroup,
  Divider,
} from '@mui/material';

import { IconHelper } from 'components/generic/IconHelper';
import { TooltipButton } from 'components/generic/TooltipButton';
import { useCosmos } from 'providers/contexts/CosmosContext';
import { articleScrollConfig } from 'screens/editorial/asset/utility/reactScrollProps';
import { BloxCustomDataProviderType } from 'utility/types/dataProvider';
import { useTranslation } from 'react-i18next';
import { useEditorBubbleMenu } from '../../context';
import { radiusDouble } from 'utility/borderRadius';

interface PreviewData {
  title: string;
  description: string;
  image: string;
  type: string;
}

const INITIAL_PREVIEW_STATE: PreviewData = {
  title: '',
  description: '',
  image: '',
  type: '',
};

const LoadingSkeleton = () => (
  <Box
    sx={(theme) => ({
      display: 'flex',
      flexDirection: 'column',
      gap: 1,
      width: 450,
      maxWidth: 'calc(100vw - 32px)',
      boxSizing: 'border-box',
      position: 'relative',
      zIndex: 1200,
      backgroundColor: theme.palette.background.paper,
      padding: 2,
      borderRadius: radiusDouble(theme),
    })}
  >
    <Box sx={{ height: 32, width: '75%', bgcolor: 'grey.300' }} />
    <Box sx={{ height: 200, width: '100%', bgcolor: 'grey.300' }} />
    <Box sx={{ height: 24, width: '100%', bgcolor: 'grey.300' }} />
    <Box sx={{ height: 24, width: '60%', bgcolor: 'grey.300' }} />
  </Box>
);

const ActionButtons = () => {
  const { editor, bubbleCleanup, setBubbleView, removeAnchor } = useEditorBubbleMenu();

  if (!editor) return null;

  return (
    <ButtonGroup
      sx={{
        backgroundColor: 'primary.main',
        '& .MuiSvgIcon-root': {
          color: 'primary.contrastText',
        },
        '& .MuiButtonBase-root': {
          px: 0.5,
          py: 0.5,
          borderRadius: 0,
        },
        '& .MuiButtonGroup-firstButton': {
          borderRight: 'none',
        },
      }}
    >
      <TooltipButton
        data-testid="bubble-menu-link-remove-btn"
        onClick={() => {
          const linkHref = editor.getAttributes('link').href;
          removeAnchor(linkHref);

          /**
           * First, remove the link from the editor
           * then set the text selection to the end of the link
           */
          editor
            .chain()
            .focus()
            .extendMarkRange('link')
            .unsetLink()
            .command(({ commands }) => {
              // Chain the commands together
              return commands.setTextSelection(editor.state.selection.to);
            })
            .run();

          // Close the bubble menu
          bubbleCleanup();
        }}
        title="Remove link"
        Icon={DeleteOutlineIcon}
      />
      <Divider
        orientation="vertical"
        flexItem
      />
      <TooltipButton
        data-testid="bubble-menu-link-edit-btn"
        onClick={() => setBubbleView('linkEditor')}
        title="Edit link"
        Icon={EditOutlinedIcon}
      />
    </ButtonGroup>
  );
};

const PreviewImage = ({
  image,
  title,
  type,
}: Pick<PreviewData, 'image' | 'title' | 'type'>) => {
  const { t } = useTranslation();
  if (!image) {
    return (
      <Box
        data-testid="bubble-menu-link-preview-no-image"
        sx={(theme) => ({
          py: 2,
          width: '100%',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          borderRadius: 2,
          backgroundColor:
            theme.palette.mode === 'light'
              ? theme.palette.grey[300]
              : theme.palette.grey[700],
        })}
      >
        <Typography sx={{ fontSize: '1rem', fontWeight: 300, color: '#939393' }}>
          {t('blox.general.no_preview')}
        </Typography>
        <IconHelper
          iconType={type}
          svgIconProps={{ fontSize: 'large', style: { fill: '#939393' } }}
        />
      </Box>
    );
  }

  return (
    <CardMedia
      data-testid="bubble-menu-link-preview-image"
      component="img"
      height="200"
      image={image}
      alt={title}
      sx={{ borderRadius: 1, mb: 2 }}
    />
  );
};

const LinkPreview = () => {
  const { activeSite } = useCosmos();
  const { editor } = useEditorBubbleMenu();
  const dataProvider = useDataProvider<BloxCustomDataProviderType>();
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(false);
  const [preview, setPreview] = useState<PreviewData>(INITIAL_PREVIEW_STATE);
  const [anchorText, setAnchorText] = useState('');
  const [url, setUrl] = useState<string | null>(null);
  const [isAnchorLink, setIsAnchorLink] = useState(false);
  const [isInternalLink, setIsInternalLink] = useState(false);
  const previousUrlRef = useRef<string | null>(null);

  useEffect(() => {
    if (!url) return;
    // Reset state when URL changes
    setIsLoading(true);
    setError(false);

    const fetchPreview = async () => {
      if (isAnchorLink) {
        try {
          // Remove the # from the URL to get the anchor ID
          const anchorId = url.substring(1);
          const element = document.getElementById(anchorId);
          if (element) {
            setAnchorText(element.textContent || 'Anchor link');
          } else {
            setAnchorText('Section link');
          }
        } catch (err) {
          setAnchorText('Section link');
        }
        setIsLoading(false);
        return;
      }

      if (!isInternalLink) {
        setIsLoading(false);
        return;
      }

      try {
        // Extract both app and UUID from TNCMS asset URL
        const match = url.match(/\/tncms\/asset\/([^/]+)\/([^/]+)$/);
        const [, app, id] = match || [];

        if (!app || !id) {
          throw new Error('Invalid URL format');
        }

        const { data } = await dataProvider.getOne(app, { id });

        setPreview({
          title: data.data.title,
          description: data.data.summary,
          image:
            data.data.previews?.thumbnail?.url ??
            data.data.previews?.size1?.url ??
            data.data.children.find((child: any) => child.type === 'image')?.previews
              .size1.url ??
            data.data?.files?.image?.previews?.thumbnail?.url,
          type: data.data.type,
        });
      } catch (err) {
        setError(true);
      } finally {
        setIsLoading(false);
      }
    };

    fetchPreview();
  }, [url, dataProvider, isInternalLink, isAnchorLink, activeSite]);

  useEffect(() => {
    if (!editor) return;
    const linkHref = editor.getAttributes('link').href;

    // Only set loading state if URL actually changed.
    if (linkHref !== previousUrlRef.current) {
      setIsLoading(true);
      previousUrlRef.current = linkHref;
    }

    setUrl(linkHref);

    // Check if the URL is an anchor link (starts with #)
    const isAnchorLink = linkHref?.startsWith('#');
    setIsAnchorLink(isAnchorLink);

    // Check if the URL is an internal link (contains activeSite)
    const isInternalLink = linkHref?.includes(activeSite) || isAnchorLink;
    setIsInternalLink(isInternalLink);
  }, [editor, activeSite, editor?.state.selection.from]);

  if (isLoading) return <LoadingSkeleton />;

  if (error || !url) {
    return (
      <Card
        data-testid="bubble-menu-link-preview-error"
        sx={(theme) => ({
          width: 450,
          maxWidth: 'calc(100vw - 32px)',
          boxSizing: 'border-box',
          position: 'relative',
          zIndex: 1200,
          backgroundColor: theme.palette.background.paper,
        })}
      >
        <CardContent
          sx={{
            '&:last-child': {
              paddingBottom: 2,
            },
          }}
        >
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
            }}
          >
            <Typography color="error">Failed to load preview</Typography>
            <ActionButtons />
          </Box>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card
      data-testid="bubble-menu-link-preview"
      sx={(theme) => ({
        width: 450,
        maxWidth: 'calc(100vw - 32px)',
        boxSizing: 'border-box',
        position: 'relative',
        zIndex: 1200,
        backgroundColor: theme.palette.background.paper,
      })}
    >
      <CardContent
        sx={{
          '&:last-child': {
            paddingBottom: 2,
          },
        }}
      >
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            mb: 0,
            gap: 1,
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, overflow: 'hidden' }}>
            {isInternalLink && !isAnchorLink && <IconHelper iconType={preview.type} />}
            <Box
              sx={{
                display: 'flex',
                flexDirection: 'column',
                gap: 1,
                overflow: 'hidden',
              }}
            >
              <Typography
                variant="body1"
                component={Link}
                href={url}
                onClick={(e) => {
                  if (isAnchorLink) {
                    e.preventDefault();
                    const anchorId = url?.substring(1);
                    scroller.scrollTo(anchorId, articleScrollConfig);
                  }
                }}
                target={'_blank'}
                sx={{
                  overflow: 'hidden',
                  whiteSpace: 'nowrap',
                  textOverflow: 'ellipsis',
                  textDecoration: isInternalLink && !isAnchorLink ? 'none' : 'underline',
                }}
              >
                {isAnchorLink ? anchorText : isInternalLink ? preview.title : url}
              </Typography>
              <Typography variant="subtitle1">
                {isAnchorLink ? 'Page Section' : isInternalLink ? 'Internal' : 'External'}{' '}
                Link
              </Typography>
            </Box>
          </Box>
          <ActionButtons />
        </Box>

        {isInternalLink && !isAnchorLink && (
          <>
            <PreviewImage
              image={preview.image}
              title={preview.title}
              type={preview.type}
            />
            <Typography
              variant="body2"
              sx={{
                color: 'text.secondary',
                mb: 2,
              }}
            >
              {(() => {
                // Create a temporary element
                const tempElement = document.createElement('div');
                // Set the innerHTML with sanitized content
                tempElement.innerHTML = DOMPurify.sanitize(preview.description);
                // Return only the text content
                return tempElement.textContent || '';
              })()}
            </Typography>
            <Link
              href={url}
              target="_blank"
              rel="noopener noreferrer"
              sx={{
                display: 'block',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
                textDecoration: 'underline',
                color: 'text.secondary',
              }}
            >
              {url}
            </Link>
          </>
        )}
      </CardContent>
    </Card>
  );
};

export default LinkPreview;
